import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { realtimeBus } from "@/lib/realtime-bus";

/**
 * Global realtime sync: listens to changes on all core tables and
 * invalidates every active React Query cache so that any insert/update/delete
 * made by another user (or in another browser tab) appears live for everyone.
 *
 * Mount this once at the layout level (Dashboard / Accountant / Admin).
 */
const CORE_TABLES = [
  "children",
  "classes",
  "staff",
  "parents",
  "attendance",
  "invoices",
  "payments",
  "expenses",
  "fee_templates",
  "lessons",
  "assignments",
  "exams",
  "exam_submissions",
  "activities",
  "live_sessions",
  "observations",
  "leave_requests",
  "messages",
  "subscriptions",
  "nursery_modules",
  "nurseries",
  "plan_prices",
  "subscription_invoices",
  "branches",
  "financial_ledger",
  "salary_payments",
  "game_results",
  "child_game_results",
  "notifications",
];

export function useGlobalRealtimeSync() {
  const queryClient = useQueryClient();
  const { profile, user } = useAuth();
  const nurseryId = profile?.nursery_id;

  useEffect(() => {
    if (!user) return;

    let invalidateTimer: ReturnType<typeof setTimeout> | null = null;
    let lastTable = "";
    const scheduleInvalidate = (table: string) => {
      lastTable = table;
      if (invalidateTimer) return;
      invalidateTimer = setTimeout(() => {
        queryClient.invalidateQueries();
        realtimeBus.emit(lastTable);
        invalidateTimer = null;
      }, 250);
    };

    const channel = supabase.channel(`global-rt-${user.id}`);

    CORE_TABLES.forEach((table) => {
      channel.on(
        "postgres_changes" as any,
        {
          event: "*",
          schema: "public",
          table,
          ...(nurseryId && table !== "nurseries" && table !== "plan_prices"
            ? { filter: `nursery_id=eq.${nurseryId}` }
            : {}),
        },
        () => scheduleInvalidate(table)
      );
    });

    channel.subscribe();

    return () => {
      if (invalidateTimer) clearTimeout(invalidateTimer);
      supabase.removeChannel(channel);
    };
  }, [user?.id, nurseryId, queryClient]);
}
